type OpenAiContentsEvent
  = | 'checkout_started'
    | 'contents_viewed'
    | 'items_added'
    | 'order_created'
    | 'page_viewed';

type OpenAiCustomerActionEvent
  = | 'appointment_scheduled'
    | 'lead_created'
    | 'registration_completed';

type OpenAiPlanEnrollmentEvent
  = | 'subscription_created'
    | 'trial_started';

type OpenAiEvent
  = | OpenAiContentsEvent
    | OpenAiCustomerActionEvent
    | OpenAiPlanEnrollmentEvent
    | 'custom';

type OpenAiMoney
  = | {
    amount?: never;
    currency?: string;
  }
  | {
    amount: number;
    currency: string;
  };

interface OpenAiContent {
  id?: string;
  name?: string;
  content_type?: string;
  quantity?: number;
  amount?: number;
  currency?: string;
}

type OpenAiContentsData = {
  type: 'contents';
  contents?: OpenAiContent[];
} & OpenAiMoney;

type OpenAiCustomerActionData = {
  type: 'customer_action';
} & OpenAiMoney;

type OpenAiPlanEnrollmentData = {
  type: 'plan_enrollment';
  plan_id?: string;
  contents?: OpenAiContent[];
} & OpenAiMoney;

type OpenAiCustomData = {
  type: 'custom';
  plan_id?: string;
  contents?: OpenAiContent[];
} & OpenAiMoney;

interface OpenAiEventDataMap {
  checkout_started: OpenAiContentsData;
  contents_viewed: OpenAiContentsData;
  items_added: OpenAiContentsData;
  order_created: OpenAiContentsData;
  page_viewed: OpenAiContentsData;

  appointment_scheduled: OpenAiCustomerActionData;
  lead_created: OpenAiCustomerActionData;
  registration_completed: OpenAiCustomerActionData;

  subscription_created: OpenAiPlanEnrollmentData;
  trial_started: OpenAiPlanEnrollmentData;

  custom: OpenAiCustomData;
}

interface OpenAiEventOptions {
  event_id?: string;
  custom_event_name?: string;
  opt_out?: boolean;
}

interface OpenAiInitOptions {
  pixelId: string;
  debug?: boolean;
}

interface OpenAiApi {
  (...args: unknown[]): void;
  q: unknown[][];
}

interface OpenAiWindow {
  oaiq?: OpenAiApi;
}

function createOpenAiApi(): OpenAiApi {
  const api = ((...args: unknown[]) => {
    api.q.push(args);
  }) as OpenAiApi;

  api.q = [];

  return api;
}

export default defineNuxtPlugin({
  name: 'kygunco:openai',
  dependsOn: ['kygunco:bus'],
  parallel: true,

  setup: () => {
    const config = useRuntimeConfig().public.openai;
    const { $bus } = useNuxtApp();

    const enabled = config.enabled && !!config.pixelId?.length;

    const openAiWindow = window as unknown as OpenAiWindow;

    const push = (...args: unknown[]) => {
      if (!enabled) {
        return;
      }

      openAiWindow.oaiq ??= createOpenAiApi();
      openAiWindow.oaiq(...args);
    };

    const measure = <TEvent extends OpenAiEvent>(
      event: TEvent,
      data: OpenAiEventDataMap[TEvent],
      options?: OpenAiEventOptions,
    ) => {
      if (options === undefined) {
        push('measure', event, data);

        return;
      }

      push('measure', event, data, options);
    };

    const toCents = (amount: number) =>
      Math.round(amount * 100);

    const { load, onError } = useScript(
      {
        src: 'https://bzrcdn.openai.com/sdk/oaiq.min.js',
        referrerpolicy: false,
        crossorigin: false,
      },
      {
        trigger: 'manual',

        beforeInit: () => {
          if (import.meta.server || !enabled) {
            return;
          }

          openAiWindow.oaiq ??= createOpenAiApi();

          push('init', {
            pixelId: config.pixelId,
          } satisfies OpenAiInitOptions);
        },
      },
    );

    /**
     * OpenAI measurement is non-critical. Fail silently when its script cannot
     * be loaded so analytics cannot interrupt the application.
     */
    onError(noop);

    $bus.once('app:interacted', () => enabled && load());

    $bus.on('product:view', (product, variation) =>
      measure('contents_viewed', {
        type: 'contents',
        contents: [
          {
            id: product.id,
            name: product.name,
            content_type: 'product',
            quantity: 1,
            amount: toCents(variation?.price ?? product.price),
            currency: 'USD',
          },
        ],
      }),
    );

    $bus.on('product:add', (product, variation, quantity) => {
      const price = variation?.price ?? product.price;
      const qty = quantity ?? 1;

      measure('items_added', {
        type: 'contents',
        amount: toCents(price * qty),
        currency: 'USD',
        contents: [
          {
            id: product.id,
            name: product.name,
            content_type: 'product',
            quantity: qty,
            amount: toCents(price),
          },
        ],
      });
    });

    $bus.on('catalog:add', (product, _collection) =>
      measure('items_added', {
        type: 'contents',
        amount: toCents(product.price),
        currency: 'USD',
        contents: [
          {
            id: product.id,
            name: product.name,
            content_type: 'product',
            quantity: 1,
            amount: toCents(product.price),
          },
        ],
      }),
    );

    $bus.on('checkout:start', items =>
      measure('checkout_started', {
        type: 'contents',
        amount: toCents(
          items.reduce(
            (total, item) => total + item.price * item.quantity,
            0,
          ),
        ),
        currency: 'USD',
        contents: items.map(item => ({
          id: item.productId,
          content_type: 'product',
          quantity: item.quantity,
          amount: toCents(item.price),
        })),
      }),
    );

    $bus.on('checkout:complete', (orders) => {
      for (const order of orders) {
        measure(
          'order_created',
          {
            type: 'contents',
            amount: toCents(order.grandTotal),
            currency: 'USD',
            contents: order.items.map(item => ({
              id: item.productId,
              content_type: 'product',
              quantity: item.quantity,
              amount: toCents(item.price),
            })),
          },
          {
            event_id: order.id,
          },
        );
      }
    });

    return {
      provide: {
        openai: {
          enabled,
          measure,
          push,
        },
      },
    };
  },
});
